import dayjs from '../util/dayjs';

import { setFacebookUser } from '../analytics/facebook';
import { setGoogleUserId } from '../analytics/firebase';
import { logError } from '../util/log';
import { getStoryblokClient } from '../util/storyblok';
import { authInfo } from './auth.duck';
import { notifyMailContactEvent } from './mailContact.duck';
import { updateUserProfile } from './user.duck';

// ================ Action types ================ //

export const STARTUP_REQUEST = 'app/startUp/STARTUP_REQUEST';
export const STARTUP_SUCCESS = 'app/startUp/STARTUP_SUCCESS';

export const STARTUP_POLICIES_REQUEST = 'app/startUp/STARTUP_POLICIES_REQUEST';
export const STARTUP_POLICIES_SUCCESS = 'app/startUp/STARTUP_POLICIES_SUCCESS';

export const SET_USER_ID_REQUEST = 'app/startUp/SET_USER_ID_REQUEST';

export const SET_ONE_SIGNAL_USER_ID_REQUEST = 'app/startUp/SET_ONE_SIGNAL_USER_ID_REQUEST';

export const SET_ONLINE_STATUS = 'app/startUp/SET_ONLINE_STATUS';

// ================ Reducer ================ //

/** @type {import('../../types/vansite').VanSite.State.StartUp} */
const initialState = {
  onlineStatus: typeof window === 'undefined' ? true : navigator.onLine,
  policiesAccepted: false,
  policiesChangedModalOpen: false,
  startUpInProgress: false,
  userIdSet: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case STARTUP_REQUEST:
      return { ...state, startUpInProgress: true };
    case STARTUP_SUCCESS:
      return { ...state, startUpInProgress: false };

    case STARTUP_POLICIES_REQUEST:
      return { ...state, policiesChangedModalOpen: true };
    case STARTUP_POLICIES_SUCCESS:
      return { ...state, policiesChangedModalOpen: false, policiesAccepted: true };

    case SET_USER_ID_REQUEST:
      return { ...state, userIdSet: true };

    case SET_ONE_SIGNAL_USER_ID_REQUEST:
      return { ...state, userIdSet: true };

    case SET_ONLINE_STATUS:
      return { ...state, onlineStatus: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const startupRequest = () => ({ type: STARTUP_REQUEST });
export const startupSuccess = () => ({ type: STARTUP_SUCCESS });
export const startupPoliciesRequest = () => ({ type: STARTUP_POLICIES_REQUEST });
export const startupPoliciesSuccess = () => ({ type: STARTUP_POLICIES_SUCCESS });
export const setUserIdRequest = () => ({ type: SET_USER_ID_REQUEST });

export const setOnlineStatus = (online) => ({ type: SET_ONLINE_STATUS, payload: online });

// ================ Thunks ================ //

export const acknowledgePolicesChange = () => async (dispatch) => {
  dispatch(
    updateUserProfile({
      privateData: {
        policyAcknowledgment: null,
        lastPolicyAcknowledged: dayjs().toISOString(),
      },
    }),
  );
  dispatch(startupPoliciesSuccess());
  dispatch(startUpVanSite());
};

const setTackingUserId = (currentUser) => (dispatch) => {
  setGoogleUserId(currentUser.id.uuid);
  setFacebookUser(currentUser);
  dispatch(setUserIdRequest());
};

const initPolicies = async (dispatch, currentUser) => {
  const storyblokClient = getStoryblokClient();

  const response = await storyblokClient.get(`cdn/datasource_entries`, {
    datasource: 'dates',
  });
  const { data } = response;
  const { datasource_entries } = data;

  const lastPrivacyPolicyChangeDate = dayjs(
    datasource_entries.find((entry) => entry.name === 'privacyPolicyChangedAt').value,
    'DD.MM.YYYY',
  );
  const lastTermsOfServiceChangeDate = dayjs(
    datasource_entries.find((entry) => entry.name === 'termsOfServiceChangedAt').value,
    'DD.MM.YYYY',
  );

  const lastPolicyAcknowledged = currentUser?.attributes?.profile.privateData?.lastPolicyAcknowledged;
  const changeDates = [lastPrivacyPolicyChangeDate, lastTermsOfServiceChangeDate];

  if (isPolicyAcknowledgmentCurrent(lastPolicyAcknowledged, changeDates)) {
    dispatch(startupPoliciesSuccess());
  } else {
    dispatch(startupPoliciesRequest()); // ends startup process, will be started again when user accepts tha policies via modal
  }
};

/**
 * True when the user acknowledged the policies after the latest change of each of them.
 * A change to any one policy asks again.
 *
 * @param {string | undefined} lastAcknowledged - ISO timestamp from privateData.lastPolicyAcknowledged
 * @param {import('dayjs').Dayjs[]} changeDates - latest change date per policy
 * @returns {boolean}
 */
export const isPolicyAcknowledgmentCurrent = (lastAcknowledged, changeDates) =>
  Boolean(lastAcknowledged) && changeDates.every((date) => dayjs(lastAcknowledged).isAfter(date));

const onlineAndOfflineEventListeners = (dispatch) => {
  window.addEventListener('online', () => {
    dispatch(setOnlineStatus(true));
  });

  window.addEventListener('offline', () => {
    dispatch(setOnlineStatus(false));
  });
};

/**
 * @param {import('@vansite/ts-sharetribe-flex-sdk').CurrentUser} currentUser
 */
export const notifyAnnualPassExpiry = (currentUser) => async (dispatch) => {
  try {
    const annualPass = currentUser?.attributes?.profile.privateData.annualPass;
    if (!annualPass) return;
    const validUntilDate = dayjs.utc(annualPass.validUntil);
    if (dayjs().utc().isAfter(validUntilDate)) {
      // Der Server verifiziert das Ablaufdatum selbst - dieser Check spart
      // nur den Request im Normalfall.
      dispatch(notifyMailContactEvent('annual-pass-expired'));
    }
  } catch (e) {
    logError(e, 'mail-annual-pass-expiry', { currentUser });
  }
};

export const initSetTackingUserId = () => async (dispatch, getState) => {
  const { currentUser } = getState().user;
  const { userIdSet } = getState().startUp;
  if (!userIdSet) {
    dispatch(setTackingUserId(currentUser));
    dispatch(setUserIdRequest());
  }
};

/**
 * @type {() => import('../../../types/vansite').AppThunkAction<Promise<void>>}
 */
export const startUpVanSite = () => async (dispatch, getState) => {
  dispatch(startupRequest());
  const { currentUser } = getState().user;
  const { policiesAccepted, userIdSet } = getState().startUp;

  try {
    // No ToS screen at app start: browsing forms no contract, the terms are agreed to at signup.
    // No location request here: features that need the position ask for it themselves
    // (map, search, distance), and the app's onboarding message explains it first.
    const allChecks = [];

    // check if user has accepted the policies
    // restarts the the startup process if the user has not accepted the policies
    if (currentUser && !policiesAccepted) {
      allChecks.push(initPolicies(dispatch, currentUser));
    }

    // set user id tracking when user is logged in and policies are accepted
    // in app or in web
    if (currentUser && policiesAccepted && !userIdSet) {
      allChecks.push(dispatch(initSetTackingUserId()));
    }

    if (currentUser) {
      allChecks.push(dispatch(notifyAnnualPassExpiry(currentUser)));
    }

    Promise.all(allChecks);

    onlineAndOfflineEventListeners(dispatch);

    dispatch(startupSuccess());
  } catch (e) {
    logError(e, 'start-up', { currentUser });
  }
};
